import React, { useState } from 'react';
import { X, Save, Plus, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const InlineEditor = ({ 
  type, 
  data, 
  onSave, 
  onClose,
  position = { top: 0, left: 0 }
}) => {
  const [formData, setFormData] = useState(data || {});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave(formData);
    toast.success('Changes saved!');
    onClose();
  };

  const renderFields = () => {
    switch (type) {
      case 'siteConfig':
        return (
          <>
            <div className="space-y-2">
              <Label>Site Name</Label>
              <Input
                value={formData.siteName || ''}
                onChange={(e) => handleChange('siteName', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Hero Title</Label>
              <Input
                value={formData.heroTitle || ''}
                onChange={(e) => handleChange('heroTitle', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Hero Background Image URL</Label>
              <Input
                value={formData.heroBackgroundImage || ''}
                onChange={(e) => handleChange('heroBackgroundImage', e.target.value)}
              />
            </div>
          </>
        );

      case 'button':
        return (
          <>
            <div className="space-y-2">
              <Label>Button Label</Label>
              <Input
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Link URL</Label>
              <Input
                value={formData.href || ''}
                onChange={(e) => handleChange('href', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={formData.color || '#5f7c8a'}
                  onChange={(e) => handleChange('color', e.target.value)}
                  className="w-12 h-10 p-1"
                />
                <Input
                  value={formData.color || ''}
                  onChange={(e) => handleChange('color', e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>
          </>
        );

      case 'card':
        return (
          <>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={formData.title || ''}
                onChange={(e) => handleChange('title', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Image URL</Label>
              <Input
                value={formData.image || ''}
                onChange={(e) => handleChange('image', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Link URL</Label>
              <Input
                value={formData.href || ''}
                onChange={(e) => handleChange('href', e.target.value)}
              />
            </div>
          </>
        );

      case 'navigation':
        return (
          <>
            <div className="space-y-2">
              <Label>Label</Label>
              <Input
                value={formData.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>URL</Label>
              <Input
                value={formData.href || ''}
                onChange={(e) => handleChange('href', e.target.value)}
              />
            </div>
          </>
        );

      default:
        return (
          <div className="space-y-2">
            <Label>Content</Label>
            <Textarea
              value={formData.content || JSON.stringify(formData, null, 2)}
              onChange={(e) => handleChange('content', e.target.value)}
              rows={4}
            />
          </div>
        );
    }
  };

  return (
    <div 
      className="fixed z-50 bg-white rounded-lg shadow-2xl border min-w-[320px] max-w-md animate-in zoom-in-95"
      style={{ 
        top: Math.min(position.top, window.innerHeight - 400),
        left: Math.min(position.left, window.innerWidth - 350)
      }}
    >
      <div className="flex items-center justify-between p-3 border-b bg-gray-50 rounded-t-lg">
        <h4 className="font-medium text-gray-800">Edit {type}</h4>
        <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded">
          <X size={18} />
        </button>
      </div>
      
      <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
        {renderFields()}
      </div>
      
      <div className="flex justify-end gap-2 p-3 border-t bg-gray-50 rounded-b-lg">
        <Button variant="outline" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave} className="bg-[#5f7c8a] hover:bg-[#4a6470]">
          <Save size={14} className="mr-1" /> Save
        </Button>
      </div>
    </div>
  );
};

export default InlineEditor;
